import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import {
  DriverViewModel,
  DriverProductQualification,
  DriverCustomerTraining,
} from "@app/shared/models/drivers/DriverViewModel.model";
import { StatesServices } from "@app/shared/services/states/states.service";
import { DriverService } from "@app/driver/shared/driver.service";

@Component({
  selector: "app-driver-compliance",
  templateUrl: "./driver-compliance.component.html",
  styleUrls: ["./driver-compliance.component.css"],
})
export class DriverComplianceComponent implements OnInit, OnChanges {
  @Input() driver: DriverViewModel;
  @Input() isEditMode: boolean;
  @Input() dateInputMask: any;
  @Input() productQualificationsList: any[];
  @Input() loadingProductQualifications: boolean = false;

  @Output() hasChanges = new EventEmitter<boolean>();

  productQualifications: DriverProductQualification[] = [];
  customerTraining: DriverCustomerTraining[] = [];
  states: { abbreviation: string; fullName: string }[] = [];
  private originalDriver: string;

  productQualificationOptions: { id: string; name: string; avatarFieldName: string }[] = [];
  qualificationOptions: string[] = ["Yes", "No", "Pre-qualified"];

  // Customer training autocomplete
  allTrainingNames: string[] = [];
  filteredTrainingNames: string[] = [];

  certifications: Array<{ label: string; field: string; date: any }> = [];

  constructor(
    private statesService: StatesServices,
    private driverService: DriverService
  ) {}

  ngOnInit() {
    this.states = this.statesService.getTwoLettersStates("US");
    this.originalDriver = JSON.stringify(this.driver);
    this.loadProductQualificationOptions();
    this.initAllProductQualifications();
    this.loadCustomerTraining();
    this.loadCustomerTrainingNames();
    this.initializeCertifications();
  }

  initializeCertifications() {
    this.certifications = [
      { label: "License", field: "licenseExpirationDate", date: this.driver.licenseExpirationDate },
      {
        label: "Hazmat Training",
        field: "hazmatTrainingExpirationDate",
        date: this.driver.hazmatTrainingExpirationDate,
      },
      {
        label: "Passport",
        field: "passportExpirationDate",
        date: this.driver.passportExpirationDate,
      },
      { label: "CGMP Training", field: "expirationDateCGMP", date: this.driver.expirationDateCGMP },
      { label: "HME", field: "hmeExpirationDate", date: this.driver.hmeExpirationDate },
      {
        label: "In-Depth Security",
        field: "inDepthSecurityExpirationDate",
        date: this.driver.inDepthSecurityExpirationDate,
      },
      {
        label: "TWIC Card",
        field: "twicCardExpirationDate",
        date: this.driver.twicCardExpirationDate,
      },
      {
        label: "Physical Exam Expiration",
        field: "physicalExamExpirationDate",
        date: this.driver.physicalExamExpirationDate,
      },
    ];
  }

  onCertificationDateChange(cert: any) {
    (this.driver as any)[cert.field] = cert.date;
    this.onFieldChange();
  }

  loadProductQualificationOptions() {
    if (this.productQualificationsList && this.productQualificationsList.length > 0) {
      this.productQualificationOptions = this.productQualificationsList.map((pq) => ({
        id: pq.id,
        name: pq.name,
        avatarFieldName: pq.avatarFieldName,
      }));
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["driver"] && !changes["driver"].firstChange) {
      this.originalDriver = JSON.stringify(this.driver);
      this.initAllProductQualifications();
      this.loadCustomerTraining();
      this.initializeCertifications();
      this.checkForChanges();
    }
  }

  initAllProductQualifications() {
    // Ensure every product has a qualification entry, defaulting to 'No'
    if (!this.driver.productQualifications || !Array.isArray(this.driver.productQualifications)) {
      this.driver.productQualifications = [];
    }
    this.productQualifications = this.productQualificationOptions.map((opt) => {
      let existing = this.driver.productQualifications.find(
        (q) => q.productQualificationId === opt.id
      );
      if (!existing) {
        existing = {
          productQualificationId: opt.id,
          productQualificationName: opt.name,
          productQualificationAvatarFieldName: opt.avatarFieldName,
          qualification: "No",
        };
        this.driver.productQualifications.push(existing);
      }
      return existing;
    });
  }

  onProductQualificationChange() {
    // Already bound directly, just trigger change detection
    this.onFieldChange();
  }

  cycleQualification(qual: DriverProductQualification) {
    // Cycle through: No -> Yes -> Pre-qualified -> No
    if (qual.qualification === "No") {
      qual.qualification = "Yes";
    } else if (qual.qualification === "Yes") {
      qual.qualification = "Pre-qualified";
    } else {
      qual.qualification = "No";
    }
    this.onProductQualificationChange();
  }

  trackByProductId(index: number, product: any): string {
    return product.productQualificationId;
  }

  // Customer Training
  loadCustomerTraining() {
    if (this.driver.customerTraining && Array.isArray(this.driver.customerTraining)) {
      this.customerTraining = [...this.driver.customerTraining];
    } else {
      this.customerTraining = [];
    }
  }

  loadCustomerTrainingNames() {
    this.driverService.getCustomerTrainingNames().subscribe({
      next: (names) => {
        this.allTrainingNames = names;
      },
      error: (error) => {
        console.error("Error loading training names:", error);
      },
    });
  }

  filterTrainingNames(event: any) {
    const query = event.query.toLowerCase();
    this.filteredTrainingNames = this.allTrainingNames.filter((name) =>
      name.toLowerCase().includes(query)
    );
    // If the user types something not in the list, add it as an option
    if (query && !this.filteredTrainingNames.some((name) => name.toLowerCase() === query)) {
      this.filteredTrainingNames.push(event.query);
    }
  }

  onCustomerTrainingChange() {
    const filtered = this.customerTraining.filter((t) => t.trainingName);
    this.driver.customerTraining = filtered.length > 0 ? filtered : [];
    this.onFieldChange();
  }

  addCustomerTraining() {
    this.customerTraining.push({ trainingName: "", qualification: "" });
  }

  removeCustomerTraining(index: number) {
    this.customerTraining.splice(index, 1);
    this.onCustomerTrainingChange();
  }

  trackByIndex(index: number): number {
    return index;
  }

  onFieldChange() {
    this.checkForChanges();
  }

  private checkForChanges() {
    const currentDriver = JSON.stringify(this.driver);
    const changed = currentDriver !== this.originalDriver;
    this.hasChanges.emit(changed);
  }

  getExpirationStatus(expirationDate: any): "expired" | "expiring-soon" | "valid" | "none" {
    if (!expirationDate) {
      return "none";
    }

    const expDate = new Date(expirationDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expDate.setHours(0, 0, 0, 0);

    if (expDate < today) {
      return "expired";
    }

    const daysUntilExpiration = Math.floor(
      (expDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (daysUntilExpiration <= 30) {
      return "expiring-soon";
    }

    return "valid";
  }

  getExpirationText(expirationDate: any): string {
    const status = this.getExpirationStatus(expirationDate);

    if (status === "expired") {
      return "EXPIRED";
    }

    if (status === "expiring-soon") {
      const expDate = new Date(expirationDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      expDate.setHours(0, 0, 0, 0);
      const daysUntilExpiration = Math.floor(
        (expDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
      );
      return `Expires in ${daysUntilExpiration} day${daysUntilExpiration !== 1 ? "s" : ""}`;
    }

    return "";
  }

  getExpirationClass(expirationDate: any): string {
    const status = this.getExpirationStatus(expirationDate);
    return status === "none" ? "" : `expiration-${status}`;
  }
}
